import type {
  ListTrackContributionsQuery,
  PaginatedResponse,
  SaveTrackContributionRequest,
  TrackContribution,
} from '@kangaroo/shared';

import { api } from './api';

function extractApiErrorMessage(error: unknown) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
  ) {
    return (error as { response: { data: { message: string } } }).response.data.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Request failed';
}

function listQueryParams(query: ListTrackContributionsQuery = {}) {
  return {
    ...(query.status ? { status: query.status } : {}),
    detail: query.detail ?? 'summary',
    ...(query.search ? { search: query.search } : {}),
    ...(query.page ? { page: query.page } : {}),
    ...(query.perPage ? { perPage: query.perPage } : {}),
  };
}

export function trackContributionDetailKey(contributionId: string | null | undefined) {
  return contributionId ? `/telemetry/contributions/${encodeURIComponent(contributionId)}` : null;
}

export function trackContributionHydrateKey(contributionIds: string[]) {
  const ids = [...new Set(contributionIds.filter(Boolean))].sort();
  return ids.length > 0 ? (['track-contribution-hydrate', ...ids] as const) : null;
}

export function trackContributionDraftsListKey(trackId: string | null | undefined) {
  return trackId ? `/telemetry/tracks/${trackId}/contributions/drafts` : null;
}

export function teamTrackContributionsListKey(status?: string, detail: ListTrackContributionsQuery['detail'] = 'summary') {
  const params = new URLSearchParams();
  if (status) {
    params.set('status', status);
  }
  params.set('detail', detail ?? 'summary');
  return `/telemetry/contributions?${params.toString()}`;
}

export function globalTrackContributionsListKey(detail: ListTrackContributionsQuery['detail'] = 'summary') {
  return `/telemetry/contributions/global?detail=${detail ?? 'summary'}&catalog=v3`;
}

export function globalTrackContributionsPageKey(
  page: number,
  perPage: number,
  detail: ListTrackContributionsQuery['detail'] = 'summary',
  search = '',
) {
  return ['track-contribution-catalog-page', detail ?? 'summary', page, perPage, search] as const;
}

export async function fetchTeamTrackContributions(
  status?: string,
  detail: ListTrackContributionsQuery['detail'] = 'summary',
): Promise<TrackContribution[]> {
  const response = await api.get<{ data: TrackContribution[] }>('/telemetry/contributions', {
    params: listQueryParams({ status: status as ListTrackContributionsQuery['status'], detail }),
  });
  return response.data.data;
}

export async function fetchGlobalTrackContributions(
  detail: ListTrackContributionsQuery['detail'] = 'summary',
): Promise<TrackContribution[]> {
  const response = await api.get<{ data: TrackContribution[] }>('/telemetry/contributions/global', {
    params: listQueryParams({ detail }),
  });
  return response.data.data;
}

export async function fetchGlobalTrackContributionsPage(
  query: Pick<ListTrackContributionsQuery, 'detail' | 'search' | 'page' | 'perPage'>,
): Promise<PaginatedResponse<TrackContribution>> {
  const response = await api.get<PaginatedResponse<TrackContribution>>(
    '/telemetry/contributions/global',
    { params: listQueryParams(query) },
  );
  return response.data;
}

export async function fetchTrackContributionById(contributionId: string): Promise<TrackContribution> {
  const response = await api.get<{ data: TrackContribution }>(
    `/telemetry/contributions/${encodeURIComponent(contributionId)}`,
  );
  return response.data.data;
}

export async function fetchTrackContributionsByIds(contributionIds: string[]): Promise<TrackContribution[]> {
  const uniqueIds = [...new Set(contributionIds.filter(Boolean))];
  if (uniqueIds.length === 0) {
    return [];
  }

  return Promise.all(uniqueIds.map((contributionId) => fetchTrackContributionById(contributionId)));
}

export function mergeContributionDetails(
  summaries: TrackContribution[] | undefined,
  ...fullItems: Array<TrackContribution | null | undefined>
): TrackContribution[] {
  const merged = new Map((summaries ?? []).map((summary) => [summary.id, summary]));
  for (const item of fullItems) {
    if (item) {
      merged.set(item.id, item);
    }
  }
  return [...merged.values()];
}

export function mergeTrackContributionCatalog(
  teamContributions: TrackContribution[] | undefined,
  globalContributions: TrackContribution[] | undefined,
): TrackContribution[] {
  const merged = new Map<string, TrackContribution>();

  for (const contribution of globalContributions ?? []) {
    merged.set(contribution.id, contribution);
  }

  for (const contribution of teamContributions ?? []) {
    merged.set(contribution.id, contribution);
  }

  return [...merged.values()].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export async function fetchTrackContributionDrafts(trackId: string): Promise<TrackContribution[]> {
  const response = await api.get<{ data: TrackContribution[] }>(
    `/telemetry/tracks/${encodeURIComponent(trackId)}/contributions/drafts`,
  );
  return response.data.data;
}

export async function fetchTrackContributionDraft(
  trackId: string,
  query: { sessionKey?: string; draftId?: string },
): Promise<TrackContribution | null> {
  try {
    const response = await api.get<{ data: TrackContribution }>(
      `/telemetry/tracks/${encodeURIComponent(trackId)}/contributions/draft`,
      { params: query },
    );
    return response.data.data;
  } catch (error) {
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      (error as { response?: { status?: number } }).response?.status === 404
    ) {
      return null;
    }

    throw new Error(extractApiErrorMessage(error));
  }
}

/** Load a session draft from the active track, then legacy catalog track ids if needed. */
export async function fetchTrackContributionDraftForSession(
  trackId: string,
  sessionKey: string,
  options?: { draftId?: string; fallbackTrackIds?: string[] },
): Promise<TrackContribution | null> {
  if (options?.draftId) {
    const direct = await fetchTrackContributionDraft(trackId, { draftId: options.draftId });
    if (direct) {
      return direct;
    }

    for (const fallbackTrackId of options.fallbackTrackIds ?? []) {
      const legacy = await fetchTrackContributionDraft(fallbackTrackId, { draftId: options.draftId });
      if (legacy) {
        return legacy;
      }
    }

    return null;
  }

  const direct = await fetchTrackContributionDraft(trackId, { sessionKey });
  if (direct) {
    return direct;
  }

  for (const fallbackTrackId of options?.fallbackTrackIds ?? []) {
    const legacy = await fetchTrackContributionDraft(fallbackTrackId, { sessionKey });
    if (legacy) {
      return legacy;
    }
  }

  return null;
}

export async function fetchTrackContributionDraftsForTracks(trackIds: string[]): Promise<TrackContribution[]> {
  const uniqueTrackIds = [...new Set(trackIds.filter(Boolean))];
  const draftLists = await Promise.all(uniqueTrackIds.map((trackId) => fetchTrackContributionDrafts(trackId)));
  const merged = new Map<string, TrackContribution>();

  for (const drafts of draftLists) {
    for (const draft of drafts) {
      merged.set(draft.id, draft);
    }
  }

  return [...merged.values()].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export async function saveTrackContributionDraft(
  trackId: string,
  body: SaveTrackContributionRequest,
): Promise<TrackContribution> {
  return saveTrackContribution(trackId, body, 'draft');
}

export async function saveTrackContribution(
  trackId: string,
  body: SaveTrackContributionRequest,
  status: TrackContribution['status'] = 'draft',
): Promise<TrackContribution> {
  try {
    const response = await api.post<{ data: TrackContribution }>(
      `/telemetry/tracks/${encodeURIComponent(trackId)}/contributions`,
      { ...body, status },
    );
    return response.data.data;
  } catch (error) {
    throw new Error(extractApiErrorMessage(error));
  }
}

export async function deleteTrackContribution(trackId: string, contributionId: string): Promise<void> {
  try {
    await api.delete(
      `/telemetry/tracks/${encodeURIComponent(trackId)}/contributions/${encodeURIComponent(contributionId)}`,
    );
  } catch (error) {
    throw new Error(extractApiErrorMessage(error));
  }
}

export async function submitTrackContribution(
  trackId: string,
  body: SaveTrackContributionRequest,
): Promise<TrackContribution> {
  const response = await api.post<{ data: TrackContribution }>(
    `/telemetry/tracks/${encodeURIComponent(trackId)}/contributions`,
    { ...body, status: 'pending' },
  );
  return response.data.data;
}

export function formatTrackContributionDraftLabel(draft: TrackContribution) {
  const name = draft.venue?.trim() || 'Untitled circuit';
  const updated = new Date(draft.updatedAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${name} · ${draft.sessionKey} · ${updated}`;
}

export function createTrackContributionDraftId() {
  return crypto.randomUUID();
}
